'use client'

import { useApi } from '@repo/api'
import { useAuth } from '@repo/auth'
import { checkAccess, permissions } from '@repo/permissions'
import {
  type CopyLoadDetailsFieldKey,
  copyLoadDetailsSettingsSchema,
  type CopyLoadDetailsSettings as CopyLoadDetailsSettingsValue,
  normalizeCopyLoadDetails,
  pageDataValueSchema,
} from '@repo/schema/page-data'
import { useMutation, useQuery, useQueryClient } from '@repo/tanstack/query'
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

export const COPY_LOAD_DETAILS_PAGE_SLUG = 'loads-copy-load-details'

export const COPY_LOAD_DETAILS_FIELD_LABELS = {
  cargoGoods: 'Goods',
  cargoPackaging: 'Packaging',
  cargoPallets: 'Pallets',
  cargoRequirements: 'Requirements',
  cargoTemperature: 'Temperature',
  cargoWeight: 'Weight',
  haulierRate: 'Carrier rate',
  routeAddress: 'Address',
  routeDate: 'Date',
  routeLocationName: 'Stop name',
  routeStopType: 'Stop type',
  routeTime: 'Time',
  shipperRate: 'Customer rate',
  vehicleType: 'Vehicle type',
} satisfies Record<CopyLoadDetailsFieldKey, string>

export type CopyLoadDetailsSettings = CopyLoadDetailsSettingsValue

export type CopyLoadDetailsSettingsContext = {
  canCopyHaulierRate: boolean
  canCopyShipperRate: boolean
  isReady: boolean
  retry: () => Promise<boolean>
  settings: CopyLoadDetailsSettings
  status: CopyLoadDetailsSettingsStatus
  update: (_patch: Partial<CopyLoadDetailsSettings>) => void
}

type CopyLoadDetailsSettingsStatus = 'error' | 'idle' | 'saved' | 'saving'
type UserId = number | string

const Context = createContext<CopyLoadDetailsSettingsContext | null>(null)

const emptyPageDataValue = () => pageDataValueSchema.parse({})

const parsePageDataValue = (value: unknown) => {
  const parsed = pageDataValueSchema.safeParse(value)
  return parsed.success ? parsed.data : emptyPageDataValue()
}

export const copyLoadDetailsCacheKey = (userId: UserId) => `copy-load-details-settings-${userId}`

const copyLoadDetailsPageData = (userId: UserId) => ({
  path: { page: COPY_LOAD_DETAILS_PAGE_SLUG, userId },
})

const readCachedSettings = (userId: UserId) => {
  if (typeof window === 'undefined') return undefined

  try {
    const cached = localStorage.getItem(copyLoadDetailsCacheKey(userId))
    if (!cached) return undefined

    const parsed = copyLoadDetailsSettingsSchema.safeParse(JSON.parse(cached))
    return parsed.success ? normalizeCopyLoadDetails(parsed.data) : undefined
  } catch (error) {
    console.warn('Unable to read copy load details settings cache', error)
    return undefined
  }
}

const writeCachedSettings = (userId: UserId, settings: CopyLoadDetailsSettings) => {
  if (typeof window === 'undefined') return

  try {
    localStorage.setItem(copyLoadDetailsCacheKey(userId), JSON.stringify(settings))
  } catch (error) {
    console.warn('Unable to cache copy load details settings', error)
  }
}

const readInitialSettings = (userId: undefined | UserId) =>
  (userId ? readCachedSettings(userId) : undefined) ?? normalizeCopyLoadDetails(undefined)

export const CopyLoadDetailsSettingsProvider = ({ children }: PropsWithChildren) => {
  const { user } = useAuth()
  const api = useApi()
  const queryClient = useQueryClient()
  const userId = user?.id
  const query = useQuery({
    ...api.users.pageData.get.queryOptions(copyLoadDetailsPageData(userId ?? 0)),
    enabled: Boolean(userId),
  })
  const [settings, setSettings] = useState<CopyLoadDetailsSettings>(() =>
    readInitialSettings(userId),
  )
  const [status, setStatus] = useState<CopyLoadDetailsSettingsStatus>('idle')
  const [hydratedUserId, setHydratedUserId] = useState<null | UserId>(null)
  const [settingsUserId, setSettingsUserId] = useState(userId)
  const [isQueryError, setIsQueryError] = useState(false)
  const activeUserIdRef = useRef<null | UserId>(null)
  const hasLocalEditsRef = useRef(false)
  const pendingSavesRef = useRef(0)
  const saveQueueRef = useRef(Promise.resolve())
  const canCopyHaulierRate = Boolean(
    user && checkAccess(user, { permissions: [permissions.loads.estimatedCostsView] }).allowed,
  )
  const canCopyShipperRate = Boolean(
    user && checkAccess(user, { permissions: [permissions.loads.financialsView] }).allowed,
  )

  if (settingsUserId !== userId) {
    setSettingsUserId(userId)
    setHydratedUserId(null)
    setStatus('idle')
    setSettings(readInitialSettings(userId))
  }

  // A failed load marks the status once; a retry may clear it while the query still holds the error.
  if (isQueryError !== query.isError) {
    setIsQueryError(query.isError)
    if (query.isError) setStatus('error')
  }

  useEffect(() => {
    activeUserIdRef.current = userId ?? null
    hasLocalEditsRef.current = false
    pendingSavesRef.current = 0
    saveQueueRef.current = Promise.resolve()
  }, [userId])

  useEffect(() => {
    if (!userId || !query.isSuccess || hasLocalEditsRef.current) return

    const currentValue = parsePageDataValue(query.data?.value)
    const hydratedSettings = normalizeCopyLoadDetails(currentValue.copyLoadDetails)

    setSettings(hydratedSettings)
    writeCachedSettings(userId, hydratedSettings)
    setHydratedUserId(userId)
  }, [query.data, query.isSuccess, userId])

  const { mutateAsync } = useMutation({
    mutationFn: ({
      settings: nextSettings,
      userId: targetUserId,
    }: {
      settings: CopyLoadDetailsSettings
      userId: UserId
    }) =>
      api.users.pageData.save.mutate({
        input: pageDataValueSchema.parse({ copyLoadDetails: nextSettings }),
        ...copyLoadDetailsPageData(targetUserId),
      }),
  })

  const isReady = Boolean(userId && hydratedUserId === userId)
  const retry = async () => {
    // `refetch` ignores `enabled`, so without a User it would request page data for User 0.
    if (!userId) return false
    setStatus('idle')
    try {
      const result = await query.refetch()
      return !result.isError
    } catch {
      return false
    }
  }

  const update = (patch: Partial<CopyLoadDetailsSettings>) => {
    if (!userId || !isReady) return

    const nextSettings = normalizeCopyLoadDetails({ ...settings, ...patch })
    const targetUserId = userId

    hasLocalEditsRef.current = true
    pendingSavesRef.current += 1
    setSettings(nextSettings)
    setStatus('saving')
    writeCachedSettings(targetUserId, nextSettings)

    const persist = async () => {
      try {
        const data = await mutateAsync({ settings: nextSettings, userId: targetUserId })
        if (activeUserIdRef.current !== targetUserId) return

        pendingSavesRef.current -= 1
        // Page Data is the User's in every Organisation scope.
        queryClient.setQueriesData(
          { queryKey: api.users.pageData.get.allScopesKey(copyLoadDetailsPageData(targetUserId)) },
          data,
        )

        if (pendingSavesRef.current > 0) return

        const persistedSettings = normalizeCopyLoadDetails(
          parsePageDataValue(data.value).copyLoadDetails,
        )
        hasLocalEditsRef.current = false
        setSettings(persistedSettings)
        setStatus('saved')
        writeCachedSettings(targetUserId, persistedSettings)
      } catch {
        if (activeUserIdRef.current !== targetUserId) return

        pendingSavesRef.current -= 1
        if (pendingSavesRef.current === 0) setStatus('error')
      }
    }

    saveQueueRef.current = saveQueueRef.current.then(persist, persist)
  }

  const value: CopyLoadDetailsSettingsContext = {
    canCopyHaulierRate,
    canCopyShipperRate,
    isReady,
    retry,
    settings,
    status,
    update,
  }

  return <Context.Provider value={value} data-sentry-element="Context.Provider" data-sentry-component="CopyLoadDetailsSettingsProvider" data-sentry-source-file="copy-load-details-settings-provider.tsx">{children}</Context.Provider>
}

export const useCopyLoadDetailsSettings = () => {
  const context = useContext(Context)
  if (!context) {
    throw new Error(
      'useCopyLoadDetailsSettings must be used within CopyLoadDetailsSettingsProvider',
    )
  }

  return context
}
